import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import StockCountStatusPill from "./StockCountStatusPill";
import {
  formatCounterCompletion,
  formatAuditorCompletion,
  formatStockCountDate,
} from "../utils/stockCountFormatters";
import { STOCK_COUNT_STATUS } from "../utils/stockCountModel";

const CANCELLABLE_STATUSES = new Set([
  STOCK_COUNT_STATUS.PENDING,
  STOCK_COUNT_STATUS.IN_PROGRESS,
  STOCK_COUNT_STATUS.REALIZED,
]);

const gridSx = {
  display: "grid",
  gridTemplateColumns:
    "minmax(120px, 0.95fr) minmax(180px, 1.2fr) minmax(180px, 1.2fr) minmax(310px, 310px)",
  columnGap: 1.5,
  alignItems: "center",
  "& > *": { minWidth: 0 },
};

const cancelButtonSx = {
  width: 34,
  height: 34,
  border: "1px solid",
  borderColor: "error.light",
  color: "error.main",
  bgcolor: "common.white",
  "&:hover": {
    bgcolor: "error.50",
    borderColor: "error.main",
  },
};

function PersonColumn({ label, name, completedAt, formatCompletion }) {
  const completion = formatCompletion(completedAt);
  const pending = !completedAt;

  return (
    <Stack spacing={0.15} sx={{ minWidth: 0, overflow: "visible" }}>
      <Typography
        sx={{ color: "neutral.700", fontSize: 11, fontWeight: 650, lineHeight: 1.3 }}
      >
        {label}
      </Typography>
      <Typography
        noWrap
        title={name || undefined}
        sx={{
          color: "primary.900",
          fontSize: 13,
          fontWeight: 700,
          lineHeight: 1.3,
        }}
      >
        {name || "—"}
      </Typography>
      <Typography
        noWrap
        title={completion}
        sx={{
          color: pending ? "neutral.600" : "neutral.700",
          fontSize: 12,
          fontWeight: pending ? 600 : 650,
          fontStyle: pending ? "italic" : "normal",
          lineHeight: 1.3,
        }}
      >
        {completion}
      </Typography>
    </Stack>
  );
}

export default function StockCountListCard({
  stockCount,
  disabled = false,
  cancelling = false,
  onView,
  onCancel,
}) {
  const canCancel = CANCELLABLE_STATUSES.has(stockCount.status);

  return (
    <Box
      sx={{
        px: 1.75,
        py: 1.1,
        bgcolor: "common.white",
        borderRadius: "18px",
        border: "1px solid rgba(0,0,0,.06)",
        boxShadow: "0px 2px 10px rgba(23, 31, 76, 0.04)",
        overflow: "visible",
        flexShrink: 0,
      }}
    >
      <Box sx={{ minWidth: 740, ...gridSx }}>
        <Stack spacing={0.15} sx={{ minWidth: 0, overflow: "visible" }}>
          <Typography
            noWrap
            title={formatStockCountDate(stockCount.deadlineAt)}
            sx={{ color: "primary.900", fontSize: 14, fontWeight: 800, lineHeight: 1.3 }}
          >
            {formatStockCountDate(stockCount.deadlineAt)}
          </Typography>
          <Typography
            sx={{
              color: "neutral.700",
              fontSize: 11,
              fontWeight: 650,
              lineHeight: 1.3,
            }}
          >
            Fecha límite
          </Typography>
        </Stack>

        <PersonColumn
          label="Contador"
          name={stockCount.counterNameSnapshot}
          completedAt={stockCount.counterCompletedAt}
          formatCompletion={formatCounterCompletion}
        />

        <PersonColumn
          label="Auditor"
          name={stockCount.auditorNameSnapshot}
          completedAt={stockCount.auditorCompletedAt}
          formatCompletion={formatAuditorCompletion}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "86px 112px 34px",
            columnGap: 1,
            justifyContent: "end",
            alignItems: "center",
            overflow: "visible",
            minWidth: 0,
          }}
        >
          <Box sx={{ justifySelf: "end" }}>
            <StockCountStatusPill status={stockCount.status} />
          </Box>
          <Button
            type="button"
            variant="outlined"
            size="small"
            disabled={disabled || cancelling}
            onClick={() => onView?.(stockCount)}
            startIcon={
              <VisibilityIcon sx={{ fontSize: 17, color: "primary.700" }} />
            }
            sx={{
              minWidth: 112,
              width: 112,
              height: 34,
              px: 1.5,
              borderRadius: "33px",
              borderColor: "secondary.300",
              bgcolor: "neutral.50",
              color: "primary.900",
              textTransform: "none",
              fontSize: 12,
              fontWeight: 800,
              whiteSpace: "nowrap",
              boxShadow: "0px 1px 6px rgba(0,0,0,.04)",
              "&:hover": {
                bgcolor: "secondary.50",
                borderColor: "secondary.400",
              },
            }}
          >
            Ver lista
          </Button>
          {canCancel ? (
            <Tooltip title={cancelling ? "Cancelando..." : "Cancelar conteo"}>
              <span>
                <IconButton
                  type="button"
                  size="small"
                  aria-label={cancelling ? "Cancelando..." : "Cancelar"}
                  disabled={disabled || cancelling}
                  onClick={() => onCancel?.(stockCount)}
                  sx={cancelButtonSx}
                >
                  {cancelling ? (
                    <CircularProgress size={15} color="inherit" />
                  ) : (
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Box aria-hidden="true" sx={{ width: 34, height: 34 }} />
          )}
        </Box>
      </Box>
    </Box>
  );
}
